import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Position,
  Handle,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Globe, Shield, Database, Cpu, Code, FileUp, AlertCircle, X, ChevronDown, ChevronRight, Activity } from 'lucide-react';
import config from '@/config';

const NODE_TYPES_CONFIG = {
  webInterface: { color: 'bg-white', icon: Globe, borderColor: 'border-gray-200', iconColor: 'text-blue-500' },
  mainService: { color: 'bg-white', icon: Code, borderColor: 'border-gray-200', iconColor: 'text-purple-500' },
  authApi: { color: 'bg-white', icon: Shield, borderColor: 'border-gray-200', iconColor: 'text-green-500' },
  database: { color: 'bg-white', icon: Database, borderColor: 'border-gray-200', iconColor: 'text-indigo-500' },
  trainingService: { color: 'bg-white', icon: Cpu, borderColor: 'border-gray-200', iconColor: 'text-orange-500' },
  importProcessor: { color: 'bg-white', icon: FileUp, borderColor: 'border-gray-200', iconColor: 'text-pink-500' }
};

// Custom Node Component
const CustomNode = ({ data }) => {
  const nodeType = NODE_TYPES_CONFIG[data.type] || NODE_TYPES_CONFIG.mainService;
  const itemCount = data.itemCount || 0;
  const IconComponent = nodeType.icon;
  const isOffline = data.status === 'offline' || data.status === 'error';

  return (
    <div className={`${nodeType.color} rounded-xl shadow-lg px-6 py-5 ${isOffline ? 'border-red-500' : nodeType.borderColor} border-2 min-w-[240px] relative hover:shadow-xl transition-all ${isOffline ? 'bg-red-50' : ''}`}>
      {data.inputs && data.inputs.map((input, idx) => (
        <Handle
          key={`input-${idx}`}
          type="target"
          position={Position.Top}
          id={`input-${idx}`}
          style={{
            left: `${(100 / (data.inputs.length + 1)) * (idx + 1)}%`,
            width: '12px',
            height: '12px',
            backgroundColor: 'white',
            border: `2px solid ${isOffline ? '#ef4444' : '#3b82f6'}`,
            top: '-6px'
          }}
          title={input.label}
        />
      ))}

      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`${isOffline ? 'text-red-500 bg-red-100' : nodeType.iconColor + ' bg-gray-50'} p-2 rounded-lg`}>
            {isOffline ? <AlertCircle size={24} /> : <IconComponent size={24} />}
          </div>
          <div>
            <div className={`font-bold text-base ${isOffline ? 'text-red-700' : 'text-gray-800'}`}>{data.title}</div>
            <div className={`text-xs mt-0.5 ${isOffline ? 'text-red-600' : 'text-gray-500'}`}>{data.description}</div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${
            data.status === 'active' ? 'bg-green-500 animate-pulse' : 
            data.status === 'offline' || data.status === 'error' ? 'bg-red-500 animate-pulse' : 
            'bg-gray-400'
          }`}></div>
          <span className={`text-xs font-medium capitalize ${isOffline ? 'text-red-600' : 'text-gray-600'}`}>
            {data.status || 'idle'}
          </span>
        </div>
        
        <div className={`flex items-center gap-2 ${isOffline ? 'bg-red-100' : 'bg-gray-100'} px-3 py-1 rounded-full`}>
          <span className={`text-xs font-semibold ${isOffline ? 'text-red-600' : 'text-gray-600'}`}>Load:</span>
          <span className={`text-base font-bold ${isOffline ? 'text-red-700' : 'text-gray-800'}`}>{itemCount}%</span>
        </div>
      </div>

      {data.outputs && data.outputs.map((output, idx) => (
        <Handle
          key={`output-${idx}`}
          type="source"
          position={Position.Bottom}
          id={`output-${idx}`}
          style={{
            left: `${(100 / (data.outputs.length + 1)) * (idx + 1)}%`,
            width: '12px',
            height: '12px',
            backgroundColor: 'white',
            border: `2px solid ${isOffline ? '#ef4444' : '#3b82f6'}`,
            bottom: '-6px'
          }}
          title={output.label}
        />
      ))}
    </div>
  );
};

// Active Worker Processing Component
const ActiveWorkerProcessing = ({ worker }) => {
  const metadata = worker.metadata || {};
  const baseWorkerId = metadata.worker_id || worker.worker_uid;
  const processingNodeId = metadata.current_node_id || 'Unknown';
  
  return (
    <div className="p-3 rounded-lg border-2 border-blue-200 bg-blue-50 mb-2 animate-pulse">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Activity className="w-4 h-4 text-blue-600 animate-spin flex-shrink-0" />
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-sm font-medium text-blue-900 truncate" title={baseWorkerId}>
              {baseWorkerId.length > 20 ? baseWorkerId.substring(0, 20) + '...' : baseWorkerId}
            </span>
            <span className="text-xs text-blue-600 font-mono truncate" title={worker.worker_uid}>
              UID: {worker.worker_uid.substring(0, 8)}...
            </span>
          </div>
        </div>
      </div>
      
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-xs text-blue-600">Processing Node:</span>
          <span className="text-xs font-semibold text-blue-900 font-mono">{processingNodeId}</span>
        </div>
        
        {metadata.total_processed !== undefined && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-blue-600">Total Processed:</span>
            <span className="text-xs font-semibold text-blue-900">{metadata.total_processed} nodes</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Worker Item Component
const WorkerItem = ({ worker }) => {
  const isOffline = worker.status === 'offline' || worker.status === 'error' || worker.status === 'degraded';
  const metadata = worker.metadata || {};
  const action = metadata.action || 'idle';
  const baseWorkerId = metadata.worker_id || worker.worker_uid;
  
  return (
    <div className={`p-3 rounded-lg border-2 ${isOffline ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'} mb-2 hover:shadow-md transition-all`}>
      {/* Worker ID Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
            worker.status === 'active' ? 'bg-green-500 animate-pulse' : 
            worker.status === 'offline' || worker.status === 'error' ? 'bg-red-500 animate-pulse' : 
            'bg-yellow-500 animate-pulse'
          }`}></div>
          <div className="flex flex-col min-w-0 flex-1">
            <span className={`text-sm font-medium ${isOffline ? 'text-red-700' : 'text-gray-800'} truncate`} title={baseWorkerId}>
              {baseWorkerId.length > 25 ? baseWorkerId.substring(0, 25) + '...' : baseWorkerId}
            </span>
            <span className="text-xs text-gray-400 font-mono truncate" title={worker.worker_uid}>
              UID: {worker.worker_uid.substring(0, 8)}...
            </span>
          </div>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${isOffline ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'} font-medium`}>
          {worker.itemCount}
        </span>
      </div>
      
      {/* Action/Status Info */}
      <div className="mb-1 flex items-center gap-2">
        <span className="text-xs text-gray-500">Status:</span>
        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
          action === 'processing_node' ? 'bg-blue-100 text-blue-700' :
          action === 'idle_waiting' ? 'bg-gray-100 text-gray-600' :
          action === 'polling_for_nodes' ? 'bg-purple-100 text-purple-600' :
          action === 'node_completed' ? 'bg-green-100 text-green-600' :
          action === 'node_failed' ? 'bg-red-100 text-red-600' :
          'bg-gray-100 text-gray-600'
        }`}>
          {action.replace(/_/g, ' ')}
        </span>
      </div>
      
      {/* Total Processed */}
      {metadata.total_processed !== undefined && (
        <div className="mb-1 flex items-center gap-2">
          <span className="text-xs text-gray-500">Processed:</span>
          <span className="text-xs font-semibold text-gray-700">{metadata.total_processed} nodes</span>
        </div>
      )}
      
      {/* Error Info */}
      {metadata.error && (
        <div className="mb-1 flex items-start gap-2">
          <span className="text-xs text-red-500 flex-shrink-0">Error:</span>
          <span className="text-xs text-red-600 break-words">{metadata.error}</span>
        </div>
      )}
      
      <div className="text-xs text-gray-400 mt-2 pt-2 border-t border-gray-100">
        Registered: {new Date(worker.registeredAt).toLocaleTimeString()}
      </div>
    </div>
  );
};

// Node Processor Group Component
const NodeProcessorGroup = ({ processorId, processorData }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { total_workers, active_workers, offline_workers, degraded_workers, total_items_processed, workers } = processorData;

  return (
    <div className="mb-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex flex-col p-3 bg-gradient-to-r from-purple-50 to-blue-50 hover:from-purple-100 hover:to-blue-100 rounded-lg transition-colors mb-2 border border-purple-200"
      >
        <div className="flex items-center justify-between w-full mb-2">
          <div className="flex items-center gap-2">
            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            <Cpu size={18} className="text-purple-600" />
            <span className="font-bold text-gray-800">{processorId}</span>
          </div>
        </div>
        
        {/* Stats Row */}
        <div className="flex items-center gap-2 text-xs w-full pl-8 flex-wrap">
          <span className="px-2 py-1 rounded-full bg-white text-gray-700 font-medium border border-gray-200">
            {total_workers} total
          </span>
          {active_workers > 0 && (
            <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium border border-green-200">
              {active_workers} active
            </span>
          )}
          {offline_workers > 0 && (
            <span className="px-2 py-1 rounded-full bg-red-100 text-red-700 font-medium border border-red-200">
              {offline_workers} offline
            </span>
          )}
          {degraded_workers > 0 && (
            <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 font-medium border border-yellow-200">
              {degraded_workers} degraded
            </span>
          )}
          <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-medium border border-blue-200">
            {total_items_processed} processed
          </span>
        </div>
      </button>
      
      {isExpanded && (
        <div className="space-y-2 pl-4">
          {workers.map((worker) => (
            <WorkerItem key={worker.worker_uid} worker={worker} />
          ))}
        </div>
      )}
    </div>
  );
};

const LuxotticaSystemStatus = () => {
  const initialNodes = [
    {
      id: 'web-interface',
      type: 'custom',
      position: { x: 400, y: 50 },
      data: {
        type: 'webInterface',
        title: 'Web Interface',
        description: 'Frontend application',
        status: 'active',
        itemCount: 45,
        inputs: [],
        outputs: [{ label: 'output' }]
      },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
    },
    {
      id: 'database',
      type: 'custom',
      position: { x: 400, y: 250 },
      data: {
        type: 'database',
        title: 'Supabase Database',
        description: 'Primary data store',
        status: 'active',
        itemCount: 67,
        inputs: [{ label: 'input' }],
        outputs: [{ label: 'output' }]
      },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
    },
    {
      id: 'main-service',
      type: 'custom',
      position: { x: 150, y: 500 },
      data: {
        type: 'mainService',
        title: 'Main Service',
        description: 'Core business logic',
        status: 'active',
        itemCount: 82,
        inputs: [{ label: 'input' }],
        outputs: [{ label: 'output' }]
      },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
    },
    {
      id: 'auth-api',
      type: 'custom',
      position: { x: 650, y: 500 },
      data: {
        type: 'authApi',
        title: 'Auth API',
        description: 'Authentication service',
        status: 'active',
        itemCount: 23,
        inputs: [{ label: 'input' }],
        outputs: [{ label: 'output' }]
      },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
    },
    {
      id: 'training-service',
      type: 'custom',
      position: { x: 150, y: 750 },
      data: {
        type: 'trainingService',
        title: 'Training + Inference Service',
        description: 'ML model operations',
        status: 'active',
        itemCount: 91,
        inputs: [{ label: 'input' }],
        outputs: [{ label: 'output' }]
      },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
    },
    {
      id: 'import-processor',
      type: 'custom',
      position: { x: 650, y: 750 },
      data: {
        type: 'importProcessor',
        title: 'Import Processor',
        description: 'Data import handler',
        status: 'active',
        itemCount: 56,
        inputs: [{ label: 'input' }],
        outputs: [{ label: 'output' }]
      },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
    }
  ];

  const initialEdges = [
    { id: 'e-web-main', source: 'web-interface', target: 'main-service', sourceHandle: 'output-0', targetHandle: 'input-0', type: 'smoothstep', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
    { id: 'e-main-auth', source: 'main-service', target: 'auth-api', sourceHandle: 'output-0', targetHandle: 'input-0', type: 'smoothstep', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
    { id: 'e-auth-main', source: 'auth-api', target: 'main-service', sourceHandle: 'output-0', targetHandle: 'input-0', type: 'smoothstep', animated: true, markerEnd: { type: MarkerType.ArrowClosed }, style: { stroke: '#10b981' } },
    { id: 'e-main-db', source: 'main-service', target: 'database', sourceHandle: 'output-0', targetHandle: 'input-0', type: 'smoothstep', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
    { id: 'e-db-main', source: 'database', target: 'main-service', sourceHandle: 'output-0', targetHandle: 'input-0', type: 'smoothstep', animated: true, markerEnd: { type: MarkerType.ArrowClosed }, style: { stroke: '#6366f1' } },
    { id: 'e-main-training', source: 'main-service', target: 'training-service', sourceHandle: 'output-0', targetHandle: 'input-0', type: 'smoothstep', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
    { id: 'e-training-main', source: 'training-service', target: 'main-service', sourceHandle: 'output-0', targetHandle: 'input-0', type: 'smoothstep', animated: true, markerEnd: { type: MarkerType.ArrowClosed }, style: { stroke: '#f59e0b' } },
    { id: 'e-db-training', source: 'database', target: 'training-service', sourceHandle: 'output-0', targetHandle: 'input-0', type: 'smoothstep', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
    { id: 'e-training-db', source: 'training-service', target: 'database', sourceHandle: 'output-0', targetHandle: 'input-0', type: 'smoothstep', animated: true, markerEnd: { type: MarkerType.ArrowClosed }, style: { stroke: '#f59e0b' } },
    { id: 'e-db-import', source: 'database', target: 'import-processor', sourceHandle: 'output-0', targetHandle: 'input-0', type: 'smoothstep', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
    { id: 'e-import-db', source: 'import-processor', target: 'database', sourceHandle: 'output-0', targetHandle: 'input-0', type: 'smoothstep', animated: true, markerEnd: { type: MarkerType.ArrowClosed }, style: { stroke: '#ec4899' } },
    { id: 'e-training-import', source: 'training-service', target: 'import-processor', sourceHandle: 'output-0', targetHandle: 'input-0', type: 'smoothstep', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
    { id: 'e-import-training', source: 'import-processor', target: 'training-service', sourceHandle: 'output-0', targetHandle: 'input-0', type: 'smoothstep', animated: true, markerEnd: { type: MarkerType.ArrowClosed }, style: { stroke: '#ec4899' } }
  ];

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [workers, setWorkers] = useState({});
  const [summary, setSummary] = useState(null);
  const [overallHealth, setOverallHealth] = useState('unknown');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const nodeTypes = useMemo(() => ({ custom: CustomNode }), []);

  // Calculate total stats across all node processors
  const workerStats = useMemo(() => {
    const stats = {
      totalProcessors: 0,
      totalWorkerInstances: 0,
      totalActive: 0,
      totalOffline: 0,
      totalDegraded: 0,
      totalItemsProcessed: 0
    };

    Object.values(workers).forEach((processorData) => {
      stats.totalProcessors++;
      stats.totalWorkerInstances += processorData.total_workers || 0;
      stats.totalActive += processorData.active_workers || 0;
      stats.totalOffline += processorData.offline_workers || 0;
      stats.totalDegraded += processorData.degraded_workers || 0;
      stats.totalItemsProcessed += processorData.total_items_processed || 0;
    });

    return stats;
  }, [workers]);

  // Get all currently processing workers
  const processingWorkers = useMemo(() => {
    const processing = [];
    Object.values(workers).forEach((processorData) => {
      processorData.workers?.forEach((worker) => {
        const metadata = worker.metadata || {};
        if (metadata.action === 'processing_node') {
          processing.push(worker);
        }
      });
    });
    return processing;
  }, [workers]);

  const updateNodesFromBackend = useCallback((backendData) => {
    setNodes((nds) =>
      nds.map((node) => {
        const nodeData = backendData[node.id];
        if (nodeData) {
          return {
            ...node,
            data: {
              ...node.data,
              status: nodeData.status || node.data.status,
              itemCount: nodeData.itemCount !== undefined ? nodeData.itemCount : node.data.itemCount,
            },
          };
        }
        return node;
      })
    );

    setEdges((eds) =>
      eds.map((edge) => {
        const sourceNode = backendData[edge.source];
        const targetNode = backendData[edge.target];
        const isOffline = 
          sourceNode?.status === 'offline' || 
          sourceNode?.status === 'error' ||
          targetNode?.status === 'offline' || 
          targetNode?.status === 'error';

        return {
          ...edge,
          animated: !isOffline,
          style: {
            ...edge.style,
            stroke: isOffline ? '#ef4444' : (edge.style?.stroke || '#3b82f6'),
          },
        };
      })
    );
  }, [setNodes, setEdges]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${config.BACKEND_API_ADDRESS}/system/status`);
        const data = await response.json();
        
        updateNodesFromBackend(data.services || {});
        setWorkers(data.workers || {});
        setSummary(data.summary || null);
        setOverallHealth(data.overall_health || 'unknown');
        setConnectionStatus('connected');
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setConnectionStatus('error');
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 3000);
    return () => clearInterval(interval);
  }, [updateNodesFromBackend]);

  const onNodeClick = useCallback((event, node) => {
    console.log('Node clicked:', node);
    alert(`${node.data.title}\n${node.data.description}\nStatus: ${node.data.status}\nLoad: ${node.data.itemCount}%`);
  }, []);

  return (
    <div className="w-full h-screen bg-gray-50 flex">
      {/* Main Flow Area */}
      <div className={`flex-1 relative transition-all duration-300 ${sidebarOpen ? 'mr-96' : 'mr-0'}`}>
        <div className="absolute top-4 right-4 z-10 bg-white rounded-lg shadow-lg px-4 py-2 flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${
            connectionStatus === 'connected' ? 'bg-green-500 animate-pulse' :
            connectionStatus === 'error' ? 'bg-red-500 animate-pulse' :
            'bg-yellow-500 animate-pulse'
          }`}></div>
          <span className="text-sm font-medium text-gray-700">
            {connectionStatus === 'connected' ? 'Connected to Backend' :
             connectionStatus === 'error' ? 'Connection Lost' :
             'Connecting...'}
          </span>
        </div>

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          proOptions={{ hideAttribution: true }}
          minZoom={0.1}
          maxZoom={3}
        >
          <Controls />
          <MiniMap 
            nodeColor={(node) => {
              const isOffline = node.data.status === 'offline' || node.data.status === 'error';
              if (isOffline) return '#ef4444';
              const config = NODE_TYPES_CONFIG[node.data.type];
              return config ? config.color.replace('bg-', '#').replace('-500', '') : '#3b82f6';
            }}
            className="bg-white"
          />
          <Background variant="dots" gap={12} size={1} />
        </ReactFlow>
      </div>

      {/* Workers Sidebar */}
      <div className={`fixed right-0 top-0 h-full w-96 bg-white border-l border-gray-200 shadow-xl transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'} z-20 flex flex-col`}>
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-xl font-bold text-gray-800">Node Processors</h2>
              <p className="text-xs text-gray-500 mt-1">
                System Health: <span className={`font-semibold ${
                  overallHealth === 'healthy' ? 'text-green-600' : 
                  overallHealth === 'degraded' ? 'text-yellow-600' : 
                  'text-red-600'
                }`}>{overallHealth}</span>
              </p>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-600" />
            </button>
          </div>
          
          {/* Overall Stats - Single Line */}
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <div className="bg-white rounded px-2 py-1 border border-gray-200">
              <span className="text-gray-500">Processors: </span>
              <span className="font-bold text-gray-800">{workerStats.totalProcessors}</span>
            </div>
            <div className="bg-white rounded px-2 py-1 border border-gray-200">
              <span className="text-gray-500">Workers: </span>
              <span className="font-bold text-gray-800">{workerStats.totalWorkerInstances}</span>
            </div>
            <div className="bg-green-50 rounded px-2 py-1 border border-green-200">
              <span className="text-green-600">Active: </span>
              <span className="font-bold text-green-700">{workerStats.totalActive}</span>
            </div>
            <div className="bg-blue-50 rounded px-2 py-1 border border-blue-200">
              <span className="text-blue-600">Processed: </span>
              <span className="font-bold text-blue-700">{workerStats.totalItemsProcessed}</span>
            </div>
            {workerStats.totalOffline > 0 && (
              <div className="bg-red-50 rounded px-2 py-1 border border-red-200">
                <span className="text-red-600">Offline: </span>
                <span className="font-bold text-red-700">{workerStats.totalOffline}</span>
              </div>
            )}
            {workerStats.totalDegraded > 0 && (
              <div className="bg-yellow-50 rounded px-2 py-1 border border-yellow-200">
                <span className="text-yellow-600">Degraded: </span>
                <span className="font-bold text-yellow-700">{workerStats.totalDegraded}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {/* Active Workers Processing Section */}
          {processingWorkers.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-blue-200">
                <Activity className="w-5 h-5 text-blue-600" />
                <h3 className="font-bold text-gray-800">Active Processing</h3>
                <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                  {processingWorkers.length}
                </span>
              </div>
              <div className="space-y-2">
                {processingWorkers.map((worker) => (
                  <ActiveWorkerProcessing key={worker.worker_uid} worker={worker} />
                ))}
              </div>
            </div>
          )}

          {/* Node Processors by ID */}
          <div>
            <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-purple-200">
              <Cpu className="w-5 h-5 text-purple-600" />
              <h3 className="font-bold text-gray-800">All Node Processors</h3>
            </div>
            {Object.keys(workers).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Cpu size={48} className="mx-auto mb-3 text-gray-300" />
                <p>No node processors detected</p>
                <p className="text-xs mt-1">Processors will appear here when active</p>
              </div>
            ) : (
              Object.entries(workers).map(([processorId, processorData]) => (
                <NodeProcessorGroup key={processorId} processorId={processorId} processorData={processorData} />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Toggle Sidebar Button */}
      {!sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="fixed right-4 top-20 z-10 bg-white rounded-lg shadow-lg px-4 py-2 hover:bg-gray-50 transition-colors flex items-center gap-2"
        >
          <Cpu size={20} className="text-purple-500" />
          <span className="text-sm font-medium text-gray-700">
            Show Workers ({workerStats.totalWorkerInstances})
          </span>
          {processingWorkers.length > 0 && (
            <span className="ml-1 px-2 py-0.5 bg-blue-500 text-white rounded-full text-xs font-semibold animate-pulse">
              {processingWorkers.length} active
            </span>
          )}
        </button>
      )}
    </div>
  );
};

export default LuxotticaSystemStatus;